import { $, findParent } from '../utils/Utils';

const tab = (() => {
  const config = {
    element: [...$('.js-tab')],
    triggers: '.js-tabTrigger',
    panels: '.js-tabPanel',
    nav: '.js-tabNav',
    navItem: '.js-tabNavItem',
    activeCN: '-active',
  };

  const current = (trigger) => {
    const parent = findParent(trigger, 'js-tab');
    return {
      panel: parent.querySelector(`${config.panels}.${config.activeCN}`),
      link: parent
        .querySelector(`${config.navItem}.${config.activeCN}`)
        .querySelector(config.triggers),
      item: parent.querySelector(`${config.navItem}.${config.activeCN}`),
    };
  };

  const panel = {
    get(el) {
      return el.querySelectorAll(config.panels);
    },
    setAttr(el, i) {
      // add necessary attribute on start
      el.setAttribute('role', 'tabpanel');
      el.setAttribute('id', `tab-panel-${i + 1}`);
      el.setAttribute('aria-labelledby', `tab-link-${i + 1}`);
      // set inactive state on all panels
      this.setInactive(el);
      // add active state on the first panel
      if (i === 0) {
        this.setActive(el);
      }
    },
    setActive(el) {
      el.setAttribute('aria-hidden', 'false');
      el.classList.add(config.activeCN);
    },
    setInactive(el) {
      if (el.length && el.length > 1) {
        // if this is an array, perform the same function for each item
        el.forEach((item) => {
          this.setInactive(item);
        });
      } else {
        el.setAttribute('aria-hidden', 'true');
        el.classList.remove(config.activeCN);
      }
    },
  };

  const trigger = {
    get(el) {
      return el.querySelectorAll(config.triggers);
    },
    setAttr(el, i) {
      // add necessary attribute on start
      el.setAttribute('role', 'tab');
      el.setAttribute('id', `tab-link-${i + 1}`);
      el.setAttribute('aria-controls', `tab-panel-${i + 1}`);
      // set inactive state on all nav items
      this.setInactive(el);
      // add active state on the first nav item
      if (i === 0) {
        this.setActive(el);
      }
    },
    setActive(el) {
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-selected', 'true');
      el.setAttribute('aria-describedby', 'tab-widget-description');
      el.parentNode.classList.add(config.activeCN);
    },
    setInactive(el) {
      if (el.length && el.length > 1) {
        // if this is an array, perform the same function for each item
        el.forEach((item) => {
          this.setInactive(item);
        });
      } else {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-selected', 'false');
        el.removeAttribute('aria-describedby');
        el.parentNode.classList.remove(config.activeCN);
      }
    },
  };

  const handleKeyDown = ({ e, t }) => {
    const keyCode = e.which;
    const navItems = t.parentNode.parentNode.querySelectorAll(config.navItem);
    const parent = t.parentNode;
    const nextTab = parent.nextElementSibling
      ? parent.nextElementSibling.querySelector(config.triggers)
      : false;
    const prevTab = parent.previousElementSibling
      ? parent.previousElementSibling.querySelector(config.triggers)
      : false;
    const firstTab = [...navItems].shift().querySelector(config.triggers);
    const lastTab = [...navItems].pop().querySelector(config.triggers);

    switch (keyCode) {
      // Left/Up
      case 37:
      case 38:
        e.preventDefault();
        e.stopPropagation();

        !prevTab ? lastTab.focus() : prevTab.focus();

        break;
      // Right/Down
      case 39:
      case 40:
        e.preventDefault();
        e.stopPropagation();

        !nextTab ? firstTab.focus() : nextTab.focus();

        break;

      // Home
      case 36:
        e.preventDefault();
        e.stopPropagation();

        firstTab.focus();

        break;

      // End
      case 35:
        e.preventDefault();
        e.stopPropagation();

        lastTab.focus();

        break;

      // Enter/Space
      case 13:
      case 32:
        e.preventDefault();
        e.stopPropagation();

        break;
      default:
        break;
    }
  };

  const handleClick = ({
    e, t, triggers, element,
  }) => {
    const panels = panel.get(element);
    const targetPanel = [...panels].filter(p =>
      p.querySelector(t.getAttribute('href')))[0];

    e.preventDefault();

    trigger.setInactive(triggers);
    trigger.setActive(t);

    panel.setInactive(panels);
    panel.setActive(targetPanel);
  };

  const setupTrigger = (element) => {
    const triggers = trigger.get(element);

    triggers.forEach((t, i) => {
      trigger.setAttr(t, i);
      t.addEventListener('click', e =>
        handleClick({
          e,
          t,
          triggers,
          element,
        }));
      t.addEventListener('focus', e =>
        handleClick({
          e,
          t,
          triggers,
          element,
        }));
      t.addEventListener('keydown', e => handleKeyDown({ e, t }));
    });
  };

  const setupPanels = (element) => {
    const panels = panel.get(element);

    panels.forEach((p, i) => {
      panel.setAttr(p, i);
    });
  };

  const setupNav = (element) => {
    const nav = element.querySelector(config.nav);
    const navItems = element.querySelectorAll(config.navItem);

    nav.setAttribute('role', 'tablist');
    navItems.forEach(item => item.setAttribute('role', 'presentation'));
  };

  const build = () => {
    config.element.forEach((element) => {
      setupNav(element);
      setupTrigger(element);
      setupPanels(element);
    });
  };

  const init = () => {
    if (config.element.length > 0) {
      build();
    }
  };

  return {
    init,
    current,
  };
})();

export default tab.init();
